/**
 * LatestSessionLiteCard — parent-facing "lite view" of each athlete's most
 * recent CHECKED-IN session. Surfaces ONLY headline lift names + current 1RM.
 * Sets/reps/drills are intentionally hidden to protect programming IP.
 *
 * Used on /dashboard (parent) and /athlete (kid in Athlete Mode).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LatestSessionLiteCard() {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/me/latest-session-lite`, { withCredentials: true });
                if (!cancelled) setRows(r.data || []);
            } catch {
                if (!cancelled) setRows([]);
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (busy) {
        return (
            <div data-testid="latest-session-lite-loading" className="border border-zinc-800 rounded-2xl bg-zinc-950/60 p-5 flex items-center gap-2 text-sm text-zinc-300">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading latest session…
            </div>
        );
    }
    if (!rows || rows.length === 0) return null;

    return (
        <div data-testid="latest-session-lite" className="space-y-3">
            {rows.map((row) => (
                <div
                    key={row.athlete_id}
                    data-testid={`lite-card-${row.athlete_id}`}
                    className="border border-zinc-800 rounded-2xl bg-gradient-to-r from-brand-navy-950/40 via-black to-brand-red/5 p-5"
                >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-brand-red/15 border border-brand-red/40 flex items-center justify-center shrink-0">
                                <Activity className="w-4 h-4 text-brand-red" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                                    Latest session
                                </div>
                                <div className="font-display text-xl tracking-wider text-white truncate">
                                    {row.athlete_name}
                                </div>
                            </div>
                        </div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">
                            {row.date ? new Date(`${row.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : ""}
                        </div>
                    </div>
                    {(!row.lifts || row.lifts.length === 0) ? (
                        <p className="mt-3 text-xs text-zinc-400">Coach focused on movement quality — no headline lifts logged.</p>
                    ) : (
                        <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-zinc-200">
                            {row.lifts.map((l, i) => (
                                <li
                                    key={`${row.athlete_id}-${i}`}
                                    data-testid={`lite-lift-${row.athlete_id}-${i}`}
                                    className="flex justify-between gap-3"
                                >
                                    <span className="truncate">{l.name}</span>
                                    <span className="text-white shrink-0">{l.max ? `${l.max} lb` : "—"}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="mt-3 text-[10px] text-zinc-500">
                        Full programming stays with the coach — these are just the headline lifts.
                    </p>
                </div>
            ))}
        </div>
    );
}
